<script setup lang="ts">
/**
 * 按鈕原件。
 *
 * 展示層元件：props 進、events 出。不碰 store、不碰 API、不碰 router。
 * （見 ARCHITECTURE.md 的 web 分層規則）
 */
withDefaults(
  defineProps<{
    type?: 'button' | 'submit'
    variant?: 'primary' | 'ghost'
    disabled?: boolean
    loading?: boolean
    block?: boolean
  }>(),
  {
    type: 'button',
    variant: 'primary',
    disabled: false,
    loading: false,
    block: false,
  },
)
</script>

<template>
  <button
    :type="type"
    :class="['base-button', `base-button--${variant}`, { 'base-button--block': block }]"
    :disabled="disabled || loading"
    :aria-busy="loading"
  >
    <span v-if="loading" class="base-button__spinner" aria-hidden="true" />
    <slot />
  </button>
</template>

<style scoped lang="scss">
.base-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: $space-2;
  padding: $space-3 $space-6;
  border: 1px solid transparent;
  border-radius: $radius-full;
  font-size: $text-base;
  font-weight: 600;
  cursor: pointer;
  transition:
    background-color $transition-fast,
    opacity $transition-fast;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &--primary {
    background: $color-action;
    color: $color-on-action;

    &:hover:not(:disabled) {
      background: $color-action-hover;
    }
  }

  &--ghost {
    background: transparent;
    // 同 BaseInput：面板內自動走亮色，面板外維持原本的深色
    border-color: var(--panel-border, #{$color-border});
    color: $color-text;

    &:hover:not(:disabled) {
      background: var(--panel-raised, #{$color-surface-raised});
    }
  }

  &--block {
    width: 100%;
  }

  &__spinner {
    width: 1em;
    height: 1em;
    border: 2px solid currentcolor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: base-button-spin 0.6s linear infinite;
  }
}

@keyframes base-button-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .base-button__spinner {
    animation: none;
  }
}
</style>
